import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { ShieldCheck, RefreshCw, Loader2, AlertTriangle, Eye, Search, Download, Ban, Receipt } from 'lucide-react';
import api from '../utils/api';

const ACTIONS = [
  { value: 'all', label: 'All actions' },
  { value: 'patient_view', label: 'Profile views' },
  { value: 'patient_search', label: 'Searches' },
  { value: 'patient_export', label: 'Exports' },
  { value: 'ledger_view', label: 'Ledger views' },
  { value: 'forbidden', label: 'Blocked attempts' },
];

const ACTION_STYLE = {
  patient_view: { cls: 'bg-sky-100 text-sky-700 border-sky-200', icon: Eye, label: 'View' },
  patient_search: { cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: Search, label: 'Search' },
  patient_export: { cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Download, label: 'Export' },
  ledger_view: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Receipt, label: 'Ledger' },
  forbidden: { cls: 'bg-rose-100 text-rose-700 border-rose-200', icon: Ban, label: 'Blocked' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
};

export default function AccessLogsPage({ user, onLogout }) {
  const [days, setDays] = useState(7);
  const [action, setAction] = useState('all');
  const [search, setSearch] = useState('');
  const [userFilter, setUserFilter] = useState(null);
  const [summary, setSummary] = useState({ users: [], total_events: 0, flagged_users: 0 });
  const [logs, setLogs] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days, limit: 300 });
      if (action !== 'all') params.append('action', action);
      if (search.trim()) params.append('search', search.trim());
      if (userFilter) params.append('user_id', userFilter);
      const [s, l] = await Promise.all([
        api.get(`/admin/access-logs/summary?days=${days}`),
        api.get(`/admin/access-logs?${params.toString()}`),
      ]);
      setSummary(s.data);
      setLogs(l.data);
    } catch (err) {
      toast.error('Could not load access log');
    } finally { setLoading(false); }
  }, [days, action, search, userFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const selectedUser = summary.users.find((u) => u.user_id === userFilter);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-6" data-testid="access-logs-page">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-7 h-7 text-cyan-700" /> Patient Data Access Log
            </h1>
            <p className="text-gray-600 mt-1">Who viewed, searched or exported patient data — and blocked attempts.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="w-36" data-testid="access-logs-days-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 24 hours</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="access-logs-refresh-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Stat label="Events logged" value={summary.total_events} testId="access-stat-events" />
          <Stat label="Staff active" value={summary.users.length} testId="access-stat-users" />
          <Stat label="Flagged for review" value={summary.flagged_users} tone={summary.flagged_users ? 'text-rose-700' : 'text-slate-900'} testId="access-stat-flagged" />
        </div>

        <Card className="p-5">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3">Per-staff activity</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b">
                <tr>
                  <th className="text-left py-2 pr-3">Staff</th>
                  <th className="text-right py-2 px-2">Views</th>
                  <th className="text-right py-2 px-2">Searches</th>
                  <th className="text-right py-2 px-2">Exports</th>
                  <th className="text-right py-2 px-2">Ledger</th>
                  <th className="text-right py-2 px-2">Blocked</th>
                  <th className="text-left py-2 pl-3">Last activity</th>
                  <th className="text-left py-2 pl-3">Flags</th>
                </tr>
              </thead>
              <tbody>
                {summary.users.length === 0 && (
                  <tr><td colSpan={8} className="py-6 text-center text-slate-500">No access events in this window.</td></tr>
                )}
                {summary.users.map((u) => (
                  <tr
                    key={u.user_id}
                    onClick={() => setUserFilter(userFilter === u.user_id ? null : u.user_id)}
                    className={`border-b last:border-0 cursor-pointer hover:bg-slate-50 ${userFilter === u.user_id ? 'bg-cyan-50' : ''}`}
                    data-testid={`access-user-row-${u.user_id}`}
                  >
                    <td className="py-2 pr-3">
                      <div className="font-medium text-gray-900">{u.user_name}</div>
                      <div className="text-xs text-slate-500">{u.user_role}</div>
                    </td>
                    <td className="text-right px-2 tabular-nums">{u.patient_view}</td>
                    <td className="text-right px-2 tabular-nums">{u.patient_search}</td>
                    <td className="text-right px-2 tabular-nums">{u.patient_export}</td>
                    <td className="text-right px-2 tabular-nums">{u.ledger_view}</td>
                    <td className={`text-right px-2 tabular-nums ${u.forbidden ? 'text-rose-700 font-semibold' : ''}`}>{u.forbidden}</td>
                    <td className="pl-3 text-slate-600 whitespace-nowrap">{fmt(u.last_activity)}</td>
                    <td className="pl-3">
                      {u.flags.length === 0 ? <span className="text-slate-400 text-xs">—</span> : u.flags.map((f, i) => (
                        <Badge key={i} className="bg-rose-100 text-rose-700 border border-rose-200 mr-1 mb-1 text-[11px]">
                          <AlertTriangle className="w-3 h-3 mr-1" />{f}
                        </Badge>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 flex-1">
              Event timeline
              {selectedUser && (
                <Badge className="ml-2 bg-cyan-100 text-cyan-800 border border-cyan-200 cursor-pointer" onClick={() => setUserFilter(null)} data-testid="access-user-filter-badge">
                  {selectedUser.user_name} ×
                </Badge>
              )}
              <span className="ml-2 text-sm font-normal text-slate-500">{logs.total} event(s)</span>
            </h2>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="w-44" data-testid="access-logs-action-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by patient, staff, path…"
              className="w-64"
              data-testid="access-logs-search-input"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b">
                <tr>
                  <th className="text-left py-2 pr-3">When</th>
                  <th className="text-left py-2 px-2">Staff</th>
                  <th className="text-left py-2 px-2">Action</th>
                  <th className="text-left py-2 px-2">Detail</th>
                  <th className="text-left py-2 pl-2">IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.items.length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center text-slate-500">No events match these filters.</td></tr>
                )}
                {logs.items.map((r) => {
                  const st = ACTION_STYLE[r.action] || { cls: 'bg-slate-100 text-slate-700', icon: Eye, label: r.action };
                  const Icon = st.icon;
                  return (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`access-log-row-${r.id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap text-slate-600">{fmt(r.timestamp)}</td>
                      <td className="px-2">
                        <div className="font-medium text-gray-900">{r.user_name}</div>
                        <div className="text-xs text-slate-500">{r.user_role}</div>
                      </td>
                      <td className="px-2">
                        <Badge className={`${st.cls} border text-[11px]`}><Icon className="w-3 h-3 mr-1" />{st.label}</Badge>
                      </td>
                      <td className="px-2 text-slate-700">
                        {r.detail || '—'}
                        {r.resource_id && <span className="ml-2 font-mono text-[10px] text-slate-400">{r.resource_id.slice(0, 8)}</span>}
                      </td>
                      <td className="pl-2 text-xs text-slate-500 font-mono">{r.ip || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}

const Stat = ({ label, value, tone = 'text-slate-900', testId }) => (
  <Card className="p-4" data-testid={testId}>
    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`text-2xl font-bold ${tone}`}>{value ?? 0}</div>
  </Card>
);
